/**
 * time-windows-editor.tsx — Sửa danh sách khung giờ (ngày trong tuần + từ giờ / đến giờ).
 * Dùng cho khung giờ chuyển người và khung giờ áp dụng "Tự động ngừng trả lời".
 */
import { Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { WEEKDAYS, type TimeWindow } from './bot-settings'

export function TimeWindowsEditor({ windows, onChange, disabled, emptyText }: { windows: TimeWindow[]; onChange: (w: TimeWindow[]) => void; disabled?: boolean; emptyText?: string }) {
  const { t } = useTranslation('aiTrainUi')
  const upd = (i: number, patch: Partial<TimeWindow>) => onChange(windows.map((w, j) => (j === i ? { ...w, ...patch } : w)))
  return (
    <div className="space-y-2">
      {windows.length === 0 && <p className="text-xs text-muted-foreground">{emptyText ?? t('timeWindows.empty')}</p>}
      {windows.map((w, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2 rounded-lg border p-2">
          <div className="flex flex-wrap gap-1">
            {WEEKDAYS.map((d) => {
              const on = w.days.includes(d.value)
              return (
                <button key={d.value} type="button" disabled={disabled}
                  onClick={() => upd(i, { days: on ? w.days.filter((x) => x !== d.value) : [...w.days, d.value] })}
                  className={cn('h-7 w-9 rounded border text-xs', on ? 'border-primary bg-primary/10 font-medium text-primary' : 'hover:bg-muted')}>
                  {d.short}
                </button>
              )
            })}
          </div>
          <Input type="time" className="h-8 w-28" value={w.start} onChange={(e) => upd(i, { start: e.target.value })} disabled={disabled} aria-label={t('timeWindows.from')} />
          <span className="text-xs text-muted-foreground">{t('timeWindows.to')}</span>
          <Input type="time" className="h-8 w-28" value={w.end} onChange={(e) => upd(i, { end: e.target.value })} disabled={disabled} aria-label={t('timeWindows.toLabel')} />
          <Button size="icon" variant="ghost" className="ml-auto h-7 w-7" onClick={() => onChange(windows.filter((_, j) => j !== i))} disabled={disabled} aria-label={t('timeWindows.remove')}>
            <Trash2 className="h-3.5 w-3.5 text-destructive" />
          </Button>
        </div>
      ))}
      <Button size="sm" variant="outline" disabled={disabled || windows.length >= 14}
        onClick={() => onChange([...windows, { days: [1, 2, 3, 4, 5], start: '08:00', end: '17:30' }])}>
        <Plus className="h-3.5 w-3.5" /> {t('timeWindows.add')}
      </Button>
      <p className="text-[11px] text-muted-foreground">{t('timeWindows.note')}</p>
    </div>
  )
}

